<template>
  <div class="margin-top">
    <v-row align="center" justify="center" class="fill-height ma-0">
      <v-col cols="12" class="text-center">
        <ResHubLogo height="250" />
        <h2 class="font-weight-bold pt-8 pb-4 ml-8">
          Please wait, loading
          <span class="dots" aria-hidden="true">{{ dotsPadded }}</span>
        </h2>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState } from "vuex";
import * as firebase from 'firebase/app';
import { getRegionName } from '@/utils/functionsRegion';
import { LoginMethodEnum } from "@/enums/LoginMethodEnum";
import { generatePkce } from "@/utils/pcsAuth/pkce";
import { getFunctionUrl, getPcsAuthorizeUrl } from "@/utils/pcsAuth/pcsAuthUrls";
import * as Sentry from "@sentry/vue";
import ResHubLogo from "@/views/login/ResHubLogo.vue";

export default {
  components: {
    ResHubLogo
  },
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      dotsCount: 0,
      intervalId: null
    };
  },
  computed: {
    ...mapState({
      facilityLoginFailed: state => state.authModule.facilityLoginFailed,
      loginFailed: state => state.authModule.loginFailed,
      maintenanceUserFailed: state => state.authModule.maintenanceUserFailed,
      currentUser: state => state.authModule.user,
    }),
    dotsPadded() {
      return '.'.repeat(this.dotsCount).padEnd(3, ' ');
    }
  },
  created() {
    if(this.currentUser){
      this.$store.dispatch("userSignOut", { noRedirect: true })
    }
    this.pcsLogin()
  },
  mounted() {
    this.intervalId = setInterval(() => {
      this.dotsCount = (this.dotsCount + 1) % 4;
    }, 500);
  },
  beforeDestroy() {
    clearInterval(this.intervalId)
  },
  methods: {
    async checkLoginType() {
      try {
        console.log('checkLoginType ...')
        this.loading = true;
        const response = await firebase.app().functions(getRegionName()).httpsCallable('staff-checkLoginMethod')({
          email: this.username.trim()
        })

        console.log('loginType: ', response)
        if (response.data.method === LoginMethodEnum.StaffHub) {
          return
        }
        else if (response.data.method === LoginMethodEnum.PCS && response.data.canSSO) {
          return await this.pcsLogin()
        }
        else if (response.data.method === LoginMethodEnum.PCS && !response.data.canSSO){
          const authError = 'Login via PCS is not available for this account. Please contact support.'
          this.$toast.open({message: authError, type: "error"});
          Sentry.captureException(`Username: ${this.username} ${authError}`);
          return
        }
      } catch (error) {
        this.loading = false;
        const authError = 'Unable to determine login method. Please contact support.'
        this.$toast.open({message: authError, type: "error"});
        Sentry.captureException(`Username: ${this.username} ${authError}`);
      }
      finally {
        this.loading = false;
      }
    },
    staffHubLogin() {
      this.loading = true;
      const username = this.username.trim()
      const password = this.password.trim()
      this.$store.dispatch("userSignIn", {
        email: username,
        password: password
      }).then((isSuccess) => {
        if (isSuccess === false && this.loginFailed) {
          throw new Error(this.$t('Login.LoginFaildForUser') + username);
        }
        else if (isSuccess === false && this.facilityLoginFailed) {
          throw new Error(this.$t('Login.NoAnyFacilityAssigned') + username);
        }

      }).finally(() => {
        this.loading = false;
      })
    },
    async pcsLogin() {
      try {
        this.loading = true;

         const { codeVerifier, codeChallenge, state } = await generatePkce();

          // Save codeVerifier to Firestore via backend
          const codeverifierFuncUrl = getFunctionUrl('pcs-codeverifier')
          await fetch(codeverifierFuncUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ state, codeVerifier }),
          });

          //Redirect to PCS Identity login
          const pcsAuthFuncUrl = getFunctionUrl('pcs-auth')
          const authUrl = new URL(getPcsAuthorizeUrl());
          authUrl.searchParams.set('client_id', 'ResHubCarePlan');
          authUrl.searchParams.set('response_type', 'code');
          authUrl.searchParams.set('redirect_uri', pcsAuthFuncUrl);
          authUrl.searchParams.set('scope', 'openid PCSClaim profile');
          authUrl.searchParams.set('code_challenge', codeChallenge);
          authUrl.searchParams.set('code_challenge_method', 'S256');
          authUrl.searchParams.set('state', state);

          window.location.href = authUrl.toString();
      } catch (error) {
        const authError = 'Could not initialize login session. Please contact support.'
        this.$toast.open({message: authError, type: "error"});
        Sentry.captureException(`Username: ${this.username} ${authError}`);
      }
      finally {
        this.loading = false;
      }
    }
  }
};
</script>
<style scoped>
.login-form{
  max-width: 450px;
}

.dots {
  display: inline-block;
  width: 3ch;
  white-space: pre;
}

.margin-top{
  margin-top: 35vh;
  transform: translateY(-50%); 
}

@media (max-height: 520px) {
  .margin-top { margin-top: 16px; transform: none; }
}
</style>
